import { isNumber } from '@qctsw/utils'
import { logicCatchFn } from '../common/formSubmit'
import { useGetRouteQuery } from '@/shared/function'
import type {
  ComplaintDetailResponse,
  CompliantChangeScoreParams,
  CompliantDatumParams,
  CompliantFeedbackParams,
  CompliantScoreParams,
  UndoComplaintResponse,
  UserComplaintResponse,
} from '@/server/types'
import {
  complainApplyCollectiveApi,
  complainApplySurveyBottomApi,
  complainUrgentApi,
  complaintDetailApi,
  compliantChangeScoreApi,
  compliantDatumApi,
  compliantFeedbackApi,
  compliantScoreApi,
  compliantWithdrawApi,
  getUserComplaintPageApi,
  undoComplaintApi,
} from '@/server'
import { usePagination } from '@/composables/usePagination'
import { LOAD_STATE } from '~/shared/types'

export function logicUserComplaintDetail() {
  const state = ref(LOAD_STATE.INIT)
  const data = ref<ComplaintDetailResponse | null>(null)

  async function getData(complaintId: string | number) {
    const res = await useLoadState({
      state,
      key: `投诉详情-${complaintId}`,
      fn: () => complaintDetailApi(complaintId),
    })
    if (res)
      data.value = res.data || []
  }

  return {
    state,
    data,
    getData,
  }
}

export function logicUserComplaintList(type: 'pageNumber' | 'up' = 'pageNumber') {
  const page = useGetRouteQuery('page', '1')

  const data = ref<UserComplaintResponse[]>([])
  const pagination = usePagination({
    type,
    onlyClient: true,
    pageSize: 10,
    request: params => getUserComplaintPageApi({ ...params }),
  })
  async function getData(page: number | string = 1) {
    const p = Number.isNaN(Number(page)) ? 1 : Number(page)
    const { list } = await pagination.loadPaginationData(p) || {}
    if (!list)
      return
    if (type === 'up')
      data.value.push(...list)
    else
      data.value = list
  }

  useActivatedWatch(() => [
    watch(page, () => {
      getData(page.value)
      window.scrollTo(0, 0)
    }),
  ])

  return {
    page,
    state: computed(() => pagination.pagination.status),
    pagination,
    data,
    getData,
  }
}

export function logicRecommendCommentComplaint() {
  const state = ref(LOAD_STATE.INIT)
  const list = ref<UndoComplaintResponse[]>([])

  async function getData(seriesId?: string | number) {
    const res = await useLoadState({
      state,
      fn: () => undoComplaintApi(seriesId),
    })
    if (res)
      list.value = res.data || []
  }

  return {
    state,
    list,
    getData,
  }
}

// 投诉操作
export function logicComplaintOperation() {
  const undoComplaintStatus = reactive<{
    visible: boolean
    id: number
    seriesId?: number
    msg: string
  }>({
    visible: false,
    id: 0,
    seriesId: undefined,
    msg: '',
  })

  // 撤销投诉
  const compliantWithdraw = async (id: number | string, seriesId?: number) => {
    if (!id) {
      useMessage.error('撤销投诉失败:投诉id为空，请刷新后重试')
      return
    }

    try {
      await compliantWithdrawApi(id)
      useMessage.success('已申请撤销投诉')
      return true
    }
    catch (err: any) {
      if (err instanceof Error) {
        const msg = getErrorMsg(err)
        useDialog.error({
          title: '撤销投诉失败',
          content: msg,
          positiveText: '确定',
          showIcon: false,
        })
      }
      else if (err.code === 450) {
        undoComplaintStatus.visible = true
        undoComplaintStatus.id = id as number
        undoComplaintStatus.seriesId = seriesId
        undoComplaintStatus.msg = err?.data || '撤销投诉失败'
      }
      return false
    }
  }

  // 投诉加急
  const complainUrgent = async (id: number | string) => {
    if (!id) {
      useMessage.error('申请投诉加急失败，请刷新后重试')
      return
    }

    await logicCatchFn({
      fn: () => complainUrgentApi(id),
      successMsg: '已申请投诉加急',
      errorTitle: '申请投诉加急失败',
    })
  }

  // 投诉打分
  const compliantScore = async (id: number | string, params: CompliantScoreParams) => {
    if (!id) {
      useMessage.error('投诉打分失败:投诉id为空，请刷新后重试')
      return false
    }

    return logicCatchFn({
      fn: () => compliantScoreApi(`${id}`, params),
      successMsg: '投诉打分成功',
      errorTitle: '投诉打分失败',
    })
  }
  // 修改投诉打分
  const compliantChangeScore = async (params: CompliantChangeScoreParams) => {
    return logicCatchFn({
      fn: () => compliantChangeScoreApi(params),
      successMsg: '投诉评分修改成功',
      errorTitle: '投诉评分修改失败',
    })
  }

  // 投诉反馈
  const compliantFeedback = async (params: CompliantFeedbackParams) => {
    return logicCatchFn({
      fn: () => compliantFeedbackApi(params),
      successMsg: '投诉反馈成功',
      errorTitle: '投诉反馈失败',
    })
  }
  // 投诉补充
  const compliantDatum = async (params: CompliantDatumParams) => {
    return logicCatchFn({
      fn: () => compliantDatumApi(params),
      successMsg: '投诉补充成功',
      errorTitle: '投诉补充失败',
    })
  }

  // 申请一追到底
  const complainApplySurveyBottom = async (id: number | string) => {
    if (!id) {
      useMessage.error('申请一追到底失败，请刷新后重试')
      return
    }

    await logicCatchFn({
      fn: () => complainApplySurveyBottomApi(id),
      successMsg: '已申请一追到底',
      errorTitle: '申请一追到底失败',
    })
  }

  // 申请加入集体
  const complainApplyCollective = async (id: number | string, collectiveId: number) => {
    if (!id || !collectiveId) {
      useMessage.error('申请加入集体失败，请刷新后重试')
      return
    }

    await logicCatchFn({
      fn: () => complainApplyCollectiveApi(id, { collectiveId }),
      successMsg: '已申请加入集体',
      errorTitle: '申请加入集体失败',
    })
  }

  return {
    undoComplaintStatus,

    compliantWithdraw,
    complainUrgent,
    compliantScore,
    compliantChangeScore,
    compliantFeedback,
    compliantDatum,
    complainApplySurveyBottom,
    complainApplyCollective,
  }
}
